import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowUpRight, Check, Star } from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { SplitWords } from '@/components/generals/split-words'
import { Marquee } from '@/components/generals/marquee'
import { Counter } from '@/components/generals/counter'
import { AccordionList } from '@/components/generals/accordion-list'
import { ProductPanel } from '@/components/commerce/product-panel'
import { useGsap } from '@/hooks/use-gsap'
import { productAssurances, productName } from '@/constants/product'
import { activeIngredients, resultStats, routineSteps, testimonials } from '@/constants/home'
import { supportFaqGroups } from '@/constants/support'
import { siteConfig } from '@/constants/site'

const shopReviews = testimonials.slice(0, 3)
const deliveryFaqs = supportFaqGroups[0].items.slice(0, 3)
const returnsFaqs = supportFaqGroups[1].items.slice(0, 3)

const Shop: FC = (): ReactNode => {
    const scope = useGsap<HTMLDivElement>(({ gsap, select, reduced }) => {
        if (reduced) return

        // The buy panel is the page's first impression, so it leads.
        gsap.timeline({ defaults: { ease: 'nuvea' } })
            .from(select('[data-shop-eyebrow]'), { y: 16, opacity: 0, duration: 0.7 })
            .from(select('[data-order-gallery]'), { y: 44, opacity: 0, duration: 1 }, '-=0.4')
            .from(
                select('[data-order-detail]'),
                { y: 26, opacity: 0, duration: 0.8, stagger: 0.06 },
                '-=0.8'
            )

        select('[data-shop-section]').forEach((section) => {
            gsap.from(section.querySelectorAll('[data-shop-reveal]'), {
                y: 38,
                opacity: 0,
                duration: 0.9,
                stagger: 0.08,
                ease: 'nuvea',
                scrollTrigger: { trigger: section, start: 'top 82%' }
            })

            gsap.from(section.querySelectorAll('[data-shop-heading] .reveal-mask > span'), {
                yPercent: 115,
                duration: 1,
                stagger: 0.05,
                ease: 'nuvea',
                scrollTrigger: { trigger: section, start: 'top 82%' }
            })
        })
    })

    return (
        <Layout>
            <Seo
                title='Shop'
                description={`One jar, five actives, one step. Buy ${productName} in 50 ml or 20 ml, with free worldwide shipping and thirty days to change your mind.`}
                ogImage='/images/product-01.png'
            />

            <div ref={scope}>
                <section id='buy' className='shell-x mx-auto w-full max-w-[1760px] scroll-mt-24 pt-10 md:pt-16'>
                    <p
                        data-shop-eyebrow=''
                        className='type-label flex w-fit items-center gap-2 rounded-full bg-sage px-3 py-1.5 text-ink'
                    >
                        <Star className='size-3 fill-ink text-ink' strokeWidth={0} aria-hidden='true' />
                        3,100+ verified reviews
                    </p>

                    <div className='mt-10 md:mt-12'>
                        <ProductPanel headingAs='h1' priority />
                    </div>
                </section>

                <div className='mt-16 border-y border-stone-line/70 bg-shell py-8 md:mt-24'>
                    <Marquee duration={38}>
                        {productAssurances.map((item) => (
                            <span
                                key={item}
                                className='type-small flex items-center gap-8 px-8 whitespace-nowrap text-slate-soft'
                            >
                                {item}

                                <span aria-hidden='true' className='block size-1 rounded-full bg-stone-line' />
                            </span>
                        ))}
                    </Marquee>
                </div>

                <section
                    data-shop-section=''
                    id='whats-inside'
                    className='shell-x mx-auto w-full max-w-[1760px] py-20 md:py-28'
                >
                    <div className='grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.1fr] lg:gap-16'>
                        <div>
                            <SplitWords
                                data-shop-heading=''
                                as='h2'
                                lines={['What is in it,', 'and why.']}
                                className='type-h1 max-w-[14ch] text-ink'
                            />

                            <p data-shop-reveal='' className='type-body mt-6 max-w-[46ch] text-slate-soft'>
                                Five actives, each at a dose the published research actually used. The full
                                formula is printed on the jar and in the details above, because a list you
                                cannot read is not transparency.
                            </p>

                            <div
                                data-shop-reveal=''
                                className='relative mt-10 aspect-[4/3] w-full overflow-hidden rounded-2xl bg-shell'
                            >
                                <Image
                                    src='/images/ingredients-lay.png'
                                    alt='The five Nuvéa actives laid out on a pale surface in soft daylight'
                                    fill
                                    sizes='(min-width: 1024px) 45vw, 100vw'
                                    className='object-cover'
                                />
                            </div>
                        </div>

                        <ul data-shop-reveal='' className='border-t border-stone-line'>
                            {activeIngredients.map((ingredient) => (
                                <li
                                    key={ingredient.name}
                                    className='flex items-baseline justify-between gap-6 border-b border-stone-line py-6'
                                >
                                    <div>
                                        <h3 className='type-h3 text-ink'>
                                            {ingredient.name}
                                        </h3>

                                        <p className='type-copy mt-1 text-slate-soft'>
                                            {ingredient.role}
                                        </p>
                                    </div>

                                    <span className='type-label shrink-0 text-slate-mute'>
                                        {ingredient.index}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                <section
                    data-shop-section=''
                    id='how-to-use'
                    className='bg-shell py-20 md:py-28'
                >
                    <div className='shell-x mx-auto w-full max-w-[1760px]'>
                        <SplitWords
                            data-shop-heading=''
                            as='h2'
                            lines={['How to use it.']}
                            className='type-h1 text-ink'
                        />

                        <ol className='mt-12 grid grid-cols-1 gap-8 md:mt-16 md:grid-cols-3 md:gap-6 lg:gap-10'>
                            {routineSteps.map((step) => (
                                <li key={step.step} data-shop-reveal='' className='flex flex-col'>
                                    <div className='relative aspect-[4/5] w-full overflow-hidden rounded-2xl bg-cream'>
                                        <Image
                                            src={step.image}
                                            alt={step.alt}
                                            fill
                                            sizes='(min-width: 768px) 33vw, 100vw'
                                            className='object-cover'
                                        />
                                    </div>

                                    <div className='mt-6 flex items-baseline justify-between gap-4 border-t border-stone-line pt-5'>
                                        <h3 className='type-h3 text-ink'>
                                            {step.title}
                                        </h3>

                                        <span className='type-label text-slate-mute'>
                                            {step.step}
                                        </span>
                                    </div>

                                    <p className='type-copy mt-4 max-w-[38ch] text-slate-soft'>
                                        {step.body}
                                    </p>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>

                <section
                    data-shop-section=''
                    id='shop-results'
                    className='shell-x mx-auto w-full max-w-[1760px] py-20 md:py-28'
                >
                    <SplitWords
                        data-shop-heading=''
                        as='h2'
                        lines={['Measured, not claimed.']}
                        className='type-h1 max-w-[16ch] text-ink'
                    />

                    <dl className='mt-12 grid grid-cols-1 gap-8 md:mt-16 md:grid-cols-3 md:gap-6 lg:gap-12'>
                        {resultStats.map((stat) => (
                            <div
                                key={stat.suffix}
                                data-shop-reveal=''
                                className='border-t border-stone-line pt-6'
                            >
                                <dt className='type-h1 text-ink'>
                                    <Counter
                                        value={stat.value}
                                        decimals={stat.decimals}
                                        suffix={stat.suffix}
                                    />
                                </dt>

                                <dd className='type-copy mt-3 max-w-[38ch] text-slate-soft'>
                                    {stat.body}
                                </dd>
                            </div>
                        ))}

                        <div data-shop-reveal='' className='border-t border-stone-line pt-6'>
                            <dt className='type-h1 text-ink'>
                                <Counter value={412} />
                            </dt>

                            <dd className='type-copy mt-3 max-w-[38ch] text-slate-soft'>
                                volunteers across six skin types, assessed over twelve weeks by people with
                                no stake in the result.
                            </dd>
                        </div>
                    </dl>
                </section>

                <section
                    data-shop-section=''
                    id='shop-reviews'
                    className='bg-sage py-20 md:py-28'
                >
                    <div className='shell-x mx-auto w-full max-w-[1760px]'>
                        <SplitWords
                            data-shop-heading=''
                            as='h2'
                            lines={['What people say', 'once they have finished a jar.']}
                            className='type-h1 max-w-[20ch] text-ink'
                        />

                        <ul className='mt-12 grid grid-cols-1 gap-6 md:mt-16 md:grid-cols-3'>
                            {shopReviews.map((review) => (
                                <li
                                    key={review.id}
                                    data-shop-reveal=''
                                    className='flex flex-col rounded-2xl bg-shell p-7 md:p-8'
                                >
                                    <div
                                        className='flex items-center gap-[3px]'
                                        role='img'
                                        aria-label='Rated five out of five stars'
                                    >
                                        {Array.from({ length: 5 }).map((_, index) => (
                                            <Star
                                                key={index}
                                                className='size-3 fill-ink text-ink'
                                                strokeWidth={0}
                                                aria-hidden='true'
                                            />
                                        ))}
                                    </div>

                                    <blockquote className='type-copy mt-5 flex-1 text-slate-soft'>
                                        &ldquo;{review.quote}&rdquo;
                                    </blockquote>

                                    <div className='mt-6 flex items-center gap-3 border-t border-stone-line pt-5'>
                                        <span className='relative size-9 overflow-hidden rounded-full'>
                                            <Image
                                                src={review.avatar}
                                                alt=''
                                                aria-hidden='true'
                                                fill
                                                sizes='36px'
                                                className='object-cover'
                                            />
                                        </span>

                                        <div>
                                            <p className='type-small text-ink'>
                                                {review.name}
                                            </p>

                                            <p className='type-micro text-slate-mute'>
                                                {review.context}
                                            </p>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                <section
                    data-shop-section=''
                    id='shop-delivery'
                    className='shell-x mx-auto w-full max-w-[1760px] py-20 md:py-28'
                >
                    <div className='grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16'>
                        <div>
                            <SplitWords
                                data-shop-heading=''
                                as='h2'
                                lines={['Getting it to you.']}
                                className='type-h2 text-ink'
                            />

                            <div data-shop-reveal='' className='mt-8'>
                                <AccordionList items={deliveryFaqs} defaultOpen={0} />
                            </div>
                        </div>

                        <div>
                            <SplitWords
                                data-shop-heading=''
                                as='h2'
                                lines={['If it is not for you.']}
                                className='type-h2 text-ink'
                            />

                            <div data-shop-reveal='' className='mt-8'>
                                <AccordionList items={returnsFaqs} defaultOpen={0} />
                            </div>
                        </div>
                    </div>

                    <p data-shop-reveal='' className='type-copy mt-12 text-slate-soft'>
                        Anything else, write to{' '}
                        <a href={`mailto:${siteConfig.email}`} className='link-wipe text-ink'>
                            {siteConfig.email}
                        </a>{' '}
                        or use the{' '}
                        <Link href='/support' className='link-wipe text-ink'>
                            support page
                        </Link>
                        . A person answers, usually within one working day.
                    </p>
                </section>

                <section
                    data-shop-section=''
                    id='shop-cta'
                    className='shell-x mx-auto w-full max-w-[1760px] pb-24 md:pb-32'
                >
                    <div className='rounded-3xl bg-ink px-6 py-14 text-center md:px-12 md:py-20'>
                        <SplitWords
                            data-shop-heading=''
                            as='h2'
                            lines={['One jar. Five actives.', 'One step.']}
                            className='type-h1 mx-auto max-w-[18ch] text-cream'
                        />

                        <ul
                            data-shop-reveal=''
                            className='mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3'
                        >
                            {['Free worldwide shipping', 'Thirty-day guarantee', 'Refillable glass'].map((item) => (
                                <li key={item} className='type-small flex items-center gap-2 text-cream/75'>
                                    <Check className='size-4 shrink-0' strokeWidth={1.8} aria-hidden='true' />
                                    {item}
                                </li>
                            ))}
                        </ul>

                        <p data-shop-reveal='' className='mt-9'>
                            <Link
                                href='#buy'
                                className='type-small group inline-flex items-center gap-2 rounded-full bg-cream px-7 py-4 text-ink transition-opacity duration-400 ease-nuvea hover:opacity-85'
                            >
                                Choose your size

                                <ArrowUpRight
                                    className='size-4 transition-transform duration-400 ease-nuvea group-hover:translate-x-0.5 group-hover:-translate-y-0.5'
                                    strokeWidth={1.6}
                                />
                            </Link>
                        </p>
                    </div>
                </section>
            </div>
        </Layout>
    )
}

export default Shop
